import * as React from "react";
import { useNotify } from "react-admin";
import {
  Box, Card, CardContent, Typography, TextField as MuiTextField, Button,
  CircularProgress, Alert, Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";
import ConfirmActionButton from "../../components/ConfirmActionButton";

const MeetingsTool = () => {
  const [roomId, setRoomId] = React.useState("");
  const [clips, setClips] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [requesting, setRequesting] = React.useState(false);
  const notify = useNotify();

  const fetchClips = async () => {
    if (!roomId.trim()) return;
    setLoading(true);
    try {
      const { json } = await httpClient(
        `${API_BASE_URL}/meetings/${encodeURIComponent(roomId)}/clips`
      );
      setClips(Array.isArray(json) ? json : json?.data || []);
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
      setClips([]);
    } finally {
      setLoading(false);
    }
  };

  // Le backend refuse (400) tout enregistrement sans consent=true
  // (@RequestParam) : l'admin doit confirmer le consentement du patient.
  // Les erreurs sont notifiées par ConfirmActionButton.
  const requestClip = async () => {
    if (!roomId.trim()) return;
    setRequesting(true);
    try {
      await httpClient(
        `${API_BASE_URL}/meetings/${encodeURIComponent(roomId)}/clip?consent=true`,
        { method: "POST" }
      );
      fetchClips();
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <VideoCallIcon /> Meetings &amp; clips vidéo
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Demander un clip de la salle vidéo et lister les clips déjà générés.
      </Alert>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <MuiTextField
              size="small" label="Room ID"
              value={roomId} onChange={(e) => setRoomId(e.target.value)}
            />
            <Button variant="contained" onClick={fetchClips} disabled={loading || !roomId.trim()}>
              {loading ? <CircularProgress size={18} /> : "Lister les clips"}
            </Button>
            <ConfirmActionButton
              label="Demander un clip"
              icon={<ContentCutIcon />}
              variant="outlined"
              color="secondary"
              disabled={requesting || !roomId.trim()}
              confirmTitle="Consentement du patient requis"
              confirmText="L'enregistrement d'un clip vidéo nécessite le consentement explicite du patient. En confirmant, vous déclarez que le patient a donné son consentement à l'enregistrement de cette séance (déclaration tracée côté serveur)."
              successMessage="Demande de clip envoyée"
              refreshOnSuccess={false}
              onConfirm={requestClip}
            />
          </Box>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Clips existants</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Durée</TableCell>
                <TableCell>URL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">Aucun clip</TableCell>
                </TableRow>
              ) : clips.map((c, i) => (
                <TableRow key={c.id || i}>
                  <TableCell>{c.id}</TableCell>
                  <TableCell>{c.status}</TableCell>
                  <TableCell>{c.duration || "—"}</TableCell>
                  <TableCell>
                    {c.videoUrl ? (
                      <a href={c.videoUrl} target="_blank" rel="noopener noreferrer">Ouvrir</a>
                    ) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default { list: MeetingsTool };
